// DeferredActionDetails.jsx
import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions, Switch,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
    deferredActionsRunById,
    getDeferredActionById, updateDeferredActionInterval,
    updateDeferredActionParamValue,
    updateDeferredActionStatus
} from "../api/AiAgentApi";
import DeferredActionLogList from "./DeferredActionLogList";
import {useNavigate} from "react-router-dom";
import {IntervalMinutesField} from "./IntervalMinutesField";

// ===== Утилиты =====
const fmtDate = (iso) => {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};
const SOURCE_COLORS = {EXTERNAL: "primary", INTERNAL: "default"};

// Показ значений вида { var: "param" } красиво
function VarView({value}) {
    if (value && typeof value === "object" && "var" in value) {
        return <code>{`{ var: "${value.var}" }`}</code>;
    }
    return <code>{JSON.stringify(value)}</code>;
}

// Упрощённый читаемый вывод JSONLogic
function ConditionReadableView({condition}) {
    let obj = condition;
    if (typeof condition === "string") {
        try {
            obj = JSON.parse(condition);
        } catch {
            return <Typography color="error">Некорректный JSON</Typography>;
        }
    }
    const text = toReadable(obj);
    return (
        <Box sx={{
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            p: 1,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.1)"
        }}>
            <Typography variant="body2" sx={{lineHeight: 1.6}}>{text}</Typography>
        </Box>
    );
}

function toReadable(node) {
    if (!node || typeof node !== "object") return String(node);
    const opKeys = Object.keys(node);
    if (opKeys.length === 0) return "";
    const op = opKeys[0];
    const args = node[op];
    switch (op) {
        case "and":
        case "or": {
            const joined = (args || []).map(toReadable).filter(Boolean);
            const symbol = op === "and" ? "&&" : "||";
            return "(" + joined.join(` ${symbol} `) + ")";
        }
        case "==":
        case "!=":
        case "===":
        case "!==":
        case ">":
        case "<":
        case ">=":
        case "<=":
            return `(${toReadable(args[0])} ${op} ${toReadable(args[1])})`;
        case "!":
            return "!(" + toReadable(args[0]) + ")";
        case "var":
            return args; // имя переменной
        default:
            if (Array.isArray(args)) return `${op}(${args.map(toReadable).join(", ")})`;
            return `${op} ${toReadable(args)}`;
    }
}

// Хук для диалога редактирования
function useEditParamDialog(initial = null) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(initial); // { rowId, scriptParamId, name, value }
    const [value, setValue] = useState("");

    const openDialog = (payload) => {
        setEditing(payload);
        setValue(payload?.value ?? "");
        setOpen(true);
    };
    const closeDialog = () => setOpen(false);

    return {open, editing, value, setValue, openDialog, closeDialog};
}

export default function DeferredActionDetails() {
    const navigate = useNavigate()

    // id из query-string: ?id=...
    const [id, setId] = useState(() => new URLSearchParams(window.location.search).get("id"));
    const [loading, setLoading] = useState(false);
    const [loadingSend, setLoadingSend] = useState(false);
    const [item, setItem] = useState(null);
    const [error, setError] = useState("");
    const editDlg = useEditParamDialog();

    useEffect(() => {
        const handler = () => setId(new URLSearchParams(window.location.search).get("id"));
        window.addEventListener("popstate", handler);
        return () => window.removeEventListener("popstate", handler);
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!id) return;
            setLoading(true);
            setError("");
            setItem(null);
            try {
                const data = await getDeferredActionById(id);
                if (mounted) setItem(data || null);
            } catch (e) {
                if (mounted) setError("Не удалось загрузить отложенное действие");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [id]);

    const script = item?.script;
    const params = script?.params || [];
    const paramById = useMemo(() => {
        const m = new Map();
        params.forEach(p => m.set(p.id, p));
        return m;
    }, [params]);

    // Разбор templateBody (массив действий)
    let templateBodyArr = [];
    try {
        if (script?.templateBody) {
            const parsed = JSON.parse(script.templateBody);
            if (Array.isArray(parsed)) templateBodyArr = parsed;
        }
    } catch { /* no-op */
    }

    async function handleSaveEditedParam() {
        const {editing, value} = editDlg;
        if (!editing) return;

        await updateDeferredActionParamValue(editing.rowId, value);

        // Оптимистично обновим локально
        setItem(prev => {
            if (!prev) return prev;
            const next = {...prev, paramValues: [...(prev.paramValues || [])]};
            const idx = next.paramValues.findIndex(v => v.id === editing.rowId);
            if (idx >= 0) {
                next.paramValues[idx] = {...next.paramValues[idx], value};
            } else {
                const j = next.paramValues.findIndex(v => v.scriptParamId === editing.scriptParamId);
                if (j >= 0) next.paramValues[j] = {...next.paramValues[j], value};
            }
            return next;
        });

        editDlg.closeDialog();
    }

    const handleRun = async () => {
        setLoadingSend(true);
        try {
            await deferredActionsRunById(id);
            alert("✅ Действие успешно выполнено");
        } catch (err) {
            console.error(err);
            alert("❌ Не удалось выполнить действие");
        } finally {
            setLoadingSend(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{mb: 2}}>Отложенное действие</Typography>

            {loading && <CircularProgress size={24}/>}
            {error && <Typography color="error" sx={{mb: 2}}>{error}</Typography>}

            {item && (
                <>
                    {/* Summary */}
                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h6">{item.name}</Typography>

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="body2">Вкл/Выкл</Typography>
                                        <Tooltip title={item.isEnabled ? "Отключить действие" : "Включить действие"}>
                                            <Switch
                                                checked={item.isEnabled}
                                                color="success"
                                                onChange={async (e) => {
                                                    const newValue = e.target.checked;
                                                    setItem(prev => ({...prev, isEnabled: newValue}));
                                                    try {
                                                        await updateDeferredActionStatus(item.id, newValue);
                                                    } catch (err) {
                                                        setItem(prev => ({...prev, isEnabled: !newValue}));
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        icon={item.isEnabled ? <CheckCircleIcon/> : <CancelIcon/>}
                                        label={item.isEnabled ? "Включено" : "Выключено"}
                                        color={item.isEnabled ? "success" : "default"}
                                        size="small"
                                    />
                                    <Typography variant="body2" sx={{opacity: 0.8}}>
                                        ID: {item.id} • Создано: {fmtDate(item.createdAt)}
                                    </Typography>
                                </Stack>
                                {item.intervalMinutes &&
                                    <IntervalMinutesField
                                        item={item}
                                        onSave={(val) => {
                                            updateDeferredActionInterval(id, val).then(r => {
                                                setItem({...item, intervalMinutes: val})
                                                alert("Success")
                                            });
                                        }}
                                    />
                                }
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<EditIcon/>}
                            onClick={() => navigate("/scripts/" + script.id + "/update")}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            Редактировать скрипт
                        </Button>

                        <div style={{height: '6px'}}/>

                        <Button
                            variant="contained"
                            color="success"
                            startIcon={!loadingSend && <PlayArrowIcon/>}
                            onClick={handleRun}
                            disabled={loadingSend}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                            }}
                        >
                            {loadingSend ? <CircularProgress size={22} color="inherit"/> : "Запустить"}
                        </Button>
                    </Stack>

                    <div style={{height: "8px"}}/>

                    {/* Script info */}
                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{mb: 1}}>Скрипт</Typography>
                            <Typography variant="body2"><b>ID:</b> {script?.id} • <b>Title:</b> {script?.title}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Params */}
                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{mb: 1}}>Параметры скрипта</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Имя</TableCell>
                                        <TableCell>Источник</TableCell>
                                        <TableCell>Обяз.</TableCell>
                                        <TableCell>Default</TableCell>
                                        <TableCell>Resolver Path</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {params.map(p => (
                                        <TableRow key={p.id}>
                                            <TableCell>{p.name}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={p.source}
                                                      color={SOURCE_COLORS[p.source] || "default"}/>
                                            </TableCell>
                                            <TableCell>{p.required ? "Да" : "Нет"}</TableCell>
                                            <TableCell><code>{p.defaultValue === null ? "null" : String(p.defaultValue)}</code></TableCell>
                                            <TableCell><code>{p.resolverPath || "—"}</code></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    {/* External values with inline edit via dialog */}
                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{mb: 1}}>Значения внешних параметров</Typography>
                            {item.paramValues?.length ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Параметр</TableCell>
                                            <TableCell>scriptParamId</TableCell>
                                            <TableCell>Значение</TableCell>
                                            <TableCell width={120}/>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {item.paramValues.map(v => {
                                            const p = paramById.get(v.scriptParamId);
                                            return (
                                                <TableRow key={v.id}>
                                                    <TableCell>{p?.name || "—"}</TableCell>
                                                    <TableCell>{v.scriptParamId}</TableCell>
                                                    <TableCell><code>{String(v.value)}</code></TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Изменить">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    editDlg.openDialog({
                                                                        rowId: v.id,
                                                                        scriptParamId: v.scriptParamId,
                                                                        name: p?.name || `param_${v.scriptParamId}`,
                                                                        value: String(v.value ?? ""),
                                                                    })
                                                                }
                                                            >
                                                                <EditIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography variant="body2" sx={{opacity: 0.8}}>
                                    Нет значений внешних параметров.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    {/* Condition */}
                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{mb: 1}}>Условие (condition)</Typography>
                            <Typography variant="caption" sx={{opacity: 0.7}}>Читаемое представление</Typography>
                            <ConditionReadableView condition={script?.condition}/>
                            <Divider sx={{my: 1.5}}/>
                            <Typography variant="caption" sx={{opacity: 0.7}}>Raw JSON</Typography>
                            <TextField
                                value={script?.condition || ""}
                                onChange={() => {
                                }}
                                fullWidth
                                multiline
                                minRows={5}
                                InputProps={{readOnly: true}}
                            />
                        </CardContent>
                    </Card>

                    {/* Template Body */}
                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{mb: 1}}>Действия (templateBody)</Typography>
                            {templateBodyArr.length ? (
                                <Stack spacing={1}>
                                    {templateBodyArr.map((act, idx) => (
                                        <Box key={idx}
                                             sx={{p: 1, borderRadius: 1, border: "1px dashed rgba(255,255,255,0.2)"}}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 0.5}}>
                                                <Typography
                                                    variant="body2"><b>#{idx + 1}</b> type: <code>{act.type}</code></Typography>
                                            </Stack>
                                            <Grid container spacing={1}>
                                                {act.body && Object.entries(act.body).map(([k, v]) => (
                                                    <Grid item xs={12} md={6} key={k}>
                                                        <Typography variant="body2">
                                                            <b>{k}:</b> <VarView value={v}/>
                                                        </Typography>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" sx={{opacity: 0.8}}>Пусто или некорректный
                                    JSON.</Typography>
                            )}

                            <Divider sx={{my: 1.5}}/>
                            <Typography variant="caption" sx={{opacity: 0.7}}>Raw JSON</Typography>
                            <TextField
                                value={script?.templateBody || ""}
                                onChange={() => {
                                }}
                                fullWidth
                                multiline
                                minRows={8}
                                InputProps={{readOnly: true}}
                            />
                        </CardContent>
                    </Card>

                    <DeferredActionLogList logs={item.logs}/>
                </>
            )}

            {/* Dialog редактирования одного значения */}
            <Dialog open={editDlg.open} onClose={editDlg.closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Изменить значение</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} sx={{mt: 1}}>
                        <Typography variant="body2" sx={{opacity: 0.8}}>
                            Параметр: <b>{editDlg.editing?.name}</b>
                        </Typography>
                        <TextField
                            autoFocus
                            label="Значение"
                            value={editDlg.value}
                            onChange={(e) => editDlg.setValue(e.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{p: 2}}>
                    <Button onClick={editDlg.closeDialog}>Отмена</Button>
                    <Button variant="contained" onClick={handleSaveEditedParam}>
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
